import React, { useEffect, useRef, useState } from 'react';
import PhoneIcon from '@mui/icons-material/Phone';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import TelegramIcon from '@mui/icons-material/Telegram';
import Badge from '@mui/material/Badge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCommentDots } from '@fortawesome/free-solid-svg-icons';
import SipClient from '../pages/new_operator/SipClient';
import Chat from './Chat';
import { greenTheme } from '../helpers/style';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import notifySound from '../assets/notify.wav';

const ACCENT = greenTheme.primary;
const WHATSAPP_GREEN = '#25D366';
const TELEGRAM_BLUE = '#0088cc';

function PhoneSlashChatIcon({ size = 56 }) {
    const iconSize = size * 0.38;
    const slashHeight = size * 0.5;
    return (
        <div
            style={{
                position: 'relative',
                width: size,
                height: size,
            }}
        >
            <div
                style={{
                    position: 'absolute',
                    top: 2.84,
                    left: 4.8,
                }}
            >
                <PhoneIcon sx={{ fontSize: iconSize, color: ACCENT }} />
            </div>
            <div
                style={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    width: 1.5,
                    height: slashHeight,
                    background: ACCENT,
                    transform: 'translate(-50%, -50%) rotate(45deg)',
                }}
            />
            <div
                style={{
                    position: 'absolute',
                    bottom: 1.84,
                    right: 5.8,
                }}
            >
                <FontAwesomeIcon icon={faCommentDots} style={{ fontSize: iconSize, color: ACCENT }} />
            </div>
        </div>
    );
}

const DOCK_SHADOW = 'rgba(0, 0, 0, 0.2) 0px 2px 1px -1px, rgba(0, 0, 0, 0.14) 0px 1px 3px 4px, rgba(0, 0, 0, 0.12) 0px 1px 3px 0px';
const CHILD_SHADOW_LIGHT = 'rgba(0, 0, 0, 0.08) 0px 1px 2px 0px';

const WRAPPER_STYLE = {
    position: 'fixed',
    bottom: 24,
    right: 24,
    zIndex: 9998,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 0,
};

const LAUNCHER_BLOCK_STYLE = (isExpanded, isHovered) => ({
    width: 56,
    height: 56,
    minHeight: 56,
    borderRadius: '50%',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgb(255, 255, 255)',
    color: ACCENT,
    transition: '0.25s',
    transform: 'scale(1)',
    boxShadow: DOCK_SHADOW,
    border: '1px solid rgb(126, 188, 7)',
});

const ICONS_WRAPPER_STYLE = (isExpanded) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    padding: '14px 12px',
    background: 'transparent',
    maxHeight: isExpanded ? 200 : 0,
    overflow: 'hidden',
    opacity: isExpanded ? 1 : 0,
    transition: 'max-height 0.35s ease-out, opacity 0.25s ease-out, padding 0.25s',
});

const ICON_BTN_STYLE = (isActive, isHovered) => ({
    width: 45,
    height: 45,
    minHeight: 45,
    borderRadius: '50%',
    border: '1px solid rgb(126, 188, 7)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgb(255, 255, 255)',
    color: ACCENT,
    transition: '0.25s',
    transform: 'scale(1)',
    boxShadow: isHovered || isActive ? DOCK_SHADOW : CHILD_SHADOW_LIGHT,
});

export default function DockLauncher({ showPhone, showChat, sipProps }) {
    const [isExpanded, setIsExpanded] = useState(false);
    const [activeView, setActiveView] = useState(null);
    const [hoverLauncher, setHoverLauncher] = useState(false);
    const [hoverPhone, setHoverPhone] = useState(false);
    const [hoverChat, setHoverChat] = useState(false);
    const [hoverWhatsapp, setHoverWhatsapp] = useState(false);
    const [hoverTelegram, setHoverTelegram] = useState(false);
    const navigate = useNavigate();
    const showWhatsapp = useSelector((state) => !!state?.users?.unreadWhatsappEnabled);
    const showTelegram = useSelector((state) => !!state?.users?.unreadTelegramEnabled);
    const whatsappUnread = useSelector((state) => Number(state?.users?.whatsappUnread || 0));
    const telegramUnread = useSelector((state) => Number(state?.users?.telegramUnread || 0));
    const audioRef = useRef(null);
    const lastNotifyAtRef = useRef(0);
    const audioUnlockedRef = useRef(false);
    const lastWhatsAppUnreadRef = useRef(0);
    const lastTelegramUnreadRef = useRef(0);

    const totalUnread = Math.max(0, Number(whatsappUnread || 0)) + Math.max(0, Number(telegramUnread || 0));

    useEffect(() => {
        audioRef.current = new Audio(notifySound);
    }, []);

    // Browsers often block sound until user interaction. Unlock once.
    useEffect(() => {
        const unlock = async () => {
            if (audioUnlockedRef.current) return;
            const a = audioRef.current;
            if (!a) return;
            try {
                a.muted = true;
                await a.play();
                a.pause();
                a.currentTime = 0;
                a.muted = false;
                audioUnlockedRef.current = true;
            } catch {
                // still locked; will retry on next interaction
            }
        };

        window.addEventListener('pointerdown', unlock, { passive: true });
        window.addEventListener('keydown', unlock, { passive: true });
        return () => {
            window.removeEventListener('pointerdown', unlock);
            window.removeEventListener('keydown', unlock);
        };
    }, []);

    const playNotifySound = () => {
        if (!audioRef.current) return;
        try {
            audioRef.current.currentTime = 0;
            audioRef.current.play().catch((e) => { console.log('notify sound error', e); });
        } catch (e) {
            console.log('notify sound error', e);
        }
    };

    // Play notify only when unread increases.
    useEffect(() => {
        if (whatsappUnread > lastWhatsAppUnreadRef.current) playNotifySound();
        lastWhatsAppUnreadRef.current = whatsappUnread;
    }, [whatsappUnread]);

    useEffect(() => {
        if (telegramUnread > lastTelegramUnreadRef.current) playNotifySound();
        lastTelegramUnreadRef.current = telegramUnread;
    }, [telegramUnread]);

    const hasAny = showPhone || showChat || showWhatsapp || showTelegram;
    if (!hasAny) return null;

    const enabledCount = [showPhone, showChat, showWhatsapp, showTelegram].filter(Boolean).length;
    const onlyChat = showChat && enabledCount === 1;
    const onlyPhone = showPhone && enabledCount === 1;
    const onlyWhatsapp = showWhatsapp && enabledCount === 1;
    const onlyTelegram = showTelegram && enabledCount === 1;
    const historyTelegram = showTelegram && showWhatsapp;
    const toggleExpand = () => setIsExpanded((prev) => !prev);
    const handlePhoneClick = () => setActiveView((prev) => (prev === 'phone' ? null : 'phone'));
    const handleChatClick = () => setActiveView((prev) => (prev === 'chat' ? null : 'chat'));
    const handleWhatsappClick = () => {
        setActiveView('whatsapp');
        navigate('/history_whatsapp');
        setIsExpanded(false);
    };

    const handleTelegramClick = () => {
        setActiveView('telegram');
        navigate('/history_telegram');
        setIsExpanded(false);
    };

    const mainButtonClick = onlyChat
        ? handleChatClick
        : onlyPhone
            ? handlePhoneClick
            : onlyWhatsapp
                ? handleWhatsappClick
                : onlyTelegram
                    ? handleTelegramClick
                    : toggleExpand;

    return (
        <>
            <div className="dock-launcher" style={WRAPPER_STYLE}>
                {enabledCount > 1 && (
                    <div
                        style={{
                            ...ICONS_WRAPPER_STYLE(isExpanded),
                            ...(isExpanded ? {} : { padding: 0, margin: 0 }),
                        }}
                    >
                       
                        {showPhone && (
                            <button
                                type="button"
                                onClick={handlePhoneClick}
                                onMouseEnter={() => setHoverPhone(true)}
                                onMouseLeave={() => setHoverPhone(false)}
                                style={ICON_BTN_STYLE(activeView === 'phone', hoverPhone)}
                                aria-label="Phone"
                                title="Phone"
                            >
                                <PhoneIcon sx={{ fontSize: 22, color: 'inherit' }} />
                            </button>
                        )}
                        {showChat && (
                            <button
                                type="button"
                                onClick={handleChatClick}
                                onMouseEnter={() => setHoverChat(true)}
                                onMouseLeave={() => setHoverChat(false)}
                                style={ICON_BTN_STYLE(activeView === 'chat', hoverChat)}
                                aria-label="Chat"
                                title="Chat"
                            >
                                <FontAwesomeIcon icon={faCommentDots} style={{ fontSize: 22, color: 'inherit' }} />
                            </button>
                        )}
                        {showWhatsapp && (
                            <button
                                type="button"
                                onClick={handleWhatsappClick}
                                onMouseEnter={() => setHoverWhatsapp(true)}
                                onMouseLeave={() => setHoverWhatsapp(false)}
                                style={ICON_BTN_STYLE(activeView === 'whatsapp', hoverWhatsapp)}
                                aria-label="WhatsApp"
                                title="WhatsApp"
                            >
                                <Badge
                                    badgeContent={whatsappUnread > 0 ? whatsappUnread : 0}
                                    invisible={whatsappUnread <= 0}
                                    sx={{
                                        '& .MuiBadge-badge': {
                                            backgroundColor: WHATSAPP_GREEN,
                                            color: '#fff',
                                        },
                                    }}
                                >
                                    <WhatsAppIcon sx={{ fontSize: 22, color: 'inherit' }} />
                                </Badge>
                            </button>
                        )}
                        {showTelegram && (
                            <button
                                type="button"
                                onClick={handleTelegramClick}
                                onMouseEnter={() => setHoverTelegram(true)}
                                onMouseLeave={() => setHoverTelegram(false)}
                                style={ICON_BTN_STYLE(activeView === 'telegram', hoverTelegram)}
                                aria-label="Telegram"
                                title="Telegram"
                            >
                                <Badge
                                    badgeContent={telegramUnread > 0 ? telegramUnread : 0}
                                    invisible={telegramUnread <= 0}
                                    sx={{
                                        '& .MuiBadge-badge': {
                                            backgroundColor: TELEGRAM_BLUE,
                                            color: '#fff',
                                        },
                                    }}
                                >
                                    <TelegramIcon sx={{ fontSize: 22, color: TELEGRAM_BLUE }} />
                                </Badge>
                            </button>
                        )}
                    </div>
                )}
                <button
                    type="button"
                    onClick={mainButtonClick}
                    onMouseEnter={() => setHoverLauncher(true)}
                    onMouseLeave={() => setHoverLauncher(false)}
                    style={LAUNCHER_BLOCK_STYLE(isExpanded && enabledCount > 1, hoverLauncher)}
                    aria-label={
                        onlyChat ? 'Chat'
                            : onlyPhone ? 'Phone'
                                : onlyWhatsapp ? 'WhatsApp'
                                    : onlyTelegram ? 'Telegram'
                                        : (isExpanded ? 'Close dock' : 'Open dock')
                    }
                    title={
                        onlyChat ? 'Chat'
                            : onlyPhone ? 'Phone'
                                : onlyWhatsapp ? 'WhatsApp'
                                    : onlyTelegram ? 'Telegram'
                                        : (isExpanded ? 'Close' : 'Open')
                    }
                >
                    <Badge
                        color="error"
                        badgeContent={totalUnread > 0 ? totalUnread : 0}
                        invisible={totalUnread <= 0}
                    >
                        {onlyChat ? (
                            <FontAwesomeIcon icon={faCommentDots} style={{ fontSize: 22, color: ACCENT }} />
                        ) : onlyPhone ? (
                            <PhoneIcon sx={{ fontSize: 24, color: ACCENT }} />
                        ) : onlyWhatsapp ? (
                            <WhatsAppIcon sx={{ fontSize: 24, color: ACCENT }} />
                        ) : onlyTelegram ? (
                            <TelegramIcon sx={{ fontSize: 24, color: ACCENT }} />
                        ) : isExpanded ? (
                            <MoreHorizIcon sx={{ fontSize: 24, color: ACCENT }} />
                        ) :
                        historyTelegram?(
                            <FontAwesomeIcon icon={faCommentDots} style={{ fontSize: 22, color: ACCENT }} />
                        ):(
                            <PhoneSlashChatIcon size={48} />
                        )}
                    </Badge>
                </button>
            </div>
            {showPhone && (
                <SipClient
                    {...sipProps}
                    controlledOpen={activeView === 'phone'}
                    onRequestClose={() => setActiveView(null)}
                />
            )}
            {showChat && (
                <Chat
                    controlledOpen={activeView === 'chat'}
                    onRequestClose={() => setActiveView(null)}
                    onRequestOpen={() => setActiveView('chat')}
                />
            )}
        </>
    );
}
